import React, { useState } from 'react';
import { CheckIcon, CopyIcon } from 'lucide-react';
type LogoVariant = {
  name: string;
  description: string;
  url: string;
  background: 'light' | 'dark';
};
const LOGOS: LogoVariant[] = [{
  name: 'Caveo Icon — Full Color',
  description: 'Standalone mark. Use for favicons, avatars, and tight spaces.',
  url: 'https://cdn.magicpatterns.com/uploads/2AUBZP8wJG1EJKucMuiKhM/Caveo_Icon_Full_Color.png',
  background: 'light'
}, {
  name: 'Caveo Logotype — Purple (Light Backgrounds)',
  description: 'Wordmark only. Use on white or light surfaces.',
  url: 'https://cdn.magicpatterns.com/uploads/h2g1uh2wjUTHUuCPxeSGhL/Caveo_Purple_Logotype_Light_Backgrounds.png',
  background: 'light'
}, {
  name: 'Caveo Logotype — White (Dark Backgrounds)',
  description: 'Wordmark only. Use on dark or photographic surfaces.',
  url: 'https://cdn.magicpatterns.com/uploads/kBJ7r4pC68rjDRjcV3JXtY/Caveo_White_Logotype_Dark_Backgrounds.png',
  background: 'dark'
}, {
  name: 'Caveo Horizontal Logo — Full Color',
  description: 'Primary lockup with icon + wordmark. Default for headers.',
  url: 'https://cdn.magicpatterns.com/uploads/7RvAqT2A1NNXJxeFUdoF8d/Caveo_Full_Color_Horizontal_Logo.png',
  background: 'light'
}];
function CopyButton({
  value
}: {
  value: string;
}) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard unavailable; silently ignore
    }
  };
  return <button type="button" onClick={handleCopy} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors" data-id="element-4366" data-mp="3bdyh917kmrry">
      {copied ? <>
          <CheckIcon size={13} className="text-green-600" data-id="element-4367" data-mp="2dykn3if6they" />
          Copied
        </> : <>
          <CopyIcon size={13} data-id="element-4368" data-mp="33kvi32pyv7p6" />
          Copy URL
        </>}
    </button>;
}
function LogoCard({
  logo
}: {
  logo: LogoVariant;
}) {
  const isDark = logo.background === 'dark';
  return <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-id="element-4369" data-mp="3cf9t2t3grw2h">
      <div className={`flex items-center justify-center p-12 ${isDark ? 'bg-[#1a0647]' : 'bg-[#f9fafb]'}`} style={{
      minHeight: '240px'
    }} data-id="element-4370" data-mp="3hdusiqaw6bpv">
        <img src={logo.url} alt={logo.name} className="max-h-32 max-w-full object-contain" data-id="element-4371" data-mp="2oonyo4mduaay" />
      </div>

      <div className="p-5 border-t border-gray-100" data-id="element-4372" data-mp="2tmz668p7g8q0">
        <h3 className="text-sm font-semibold text-[#111111] mb-1" data-id="element-4373" data-mp="2ymty8fkpv4cn">
          {logo.name}
        </h3>
        <p className="text-xs text-gray-500 mb-3" data-id="element-4374" data-mp="8xi3tmgppq7z">{logo.description}</p>

        <div className="flex items-center gap-2" data-id="element-4375" data-mp="8zm9a8u9exad">
          <code className="flex-1 text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded-md px-2.5 py-1.5 truncate font-mono" data-id="element-4376" data-mp="dvtf81mno8sb">
            {logo.url}
          </code>
          <CopyButton value={logo.url} data-id="element-4377" data-mp="1tfu3lkqc908x" />
        </div>

        <div className="mt-2" data-id="element-4378" data-mp="1tg2dd3r5zpfw">
          <a href={logo.url} target="_blank" rel="noreferrer" className="text-xs text-[#631bb7] hover:underline font-medium" data-id="element-4379" data-mp="1oou6aedb4g5w">
            Open in new tab ↗
          </a>
        </div>
      </div>
    </div>;
}
export function LogoLibraryPage() {
  return <main className="w-full min-h-screen bg-white" data-id="element-4380" data-mp="1apcbnidby8om">
      <div className="max-w-5xl mx-auto px-6 py-12 md:py-16" data-id="element-4381" data-mp="1anrhhtuxgrra">
        <header className="mb-10" data-id="element-4382" data-mp="15p6ifxrewe4f">
          <p className="text-xs font-semibold tracking-wider uppercase text-[#631bb7] mb-2" data-id="element-4383" data-mp="15q85l0lpcr2i">
            Design System · Test Page
          </p>
          <h1 className="text-3xl md:text-4xl font-semibold text-[#111111] tracking-tight mb-3" data-id="element-4384" data-mp="303r0e2oclvk4">
            Caveo Logo Library
          </h1>
          <p className="text-base text-gray-500 max-w-2xl" data-id="element-4385" data-mp="2v5fo7lakz8yq">
            All approved Caveo logo variants. Click "Copy URL" to grab the asset
            link, or open the file in a new tab to download.
          </p>
        </header>

        <section className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4386" data-mp="382cdjlfh18bi">
          {LOGOS.map(logo => <LogoCard key={logo.url} logo={logo} data-id="element-4387" data-mp="2y6isfdnos6i4" />)}
        </section>
      </div>
    </main>;
}